import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { reqNomenc, reqNomencList } from 'src/api/nomenclature';
import { usePreferencesStore } from 'src/stores/usePreferencesStore';
import type { Nomenclature, NomenclatureNode } from 'types/app/nomenclature';

function applyInfoToChildren(node: NomenclatureNode): void {
  if (!node.children) return;
  node.children.forEach((child: NomenclatureNode) => {
    if (!child.cet && node.cet) {
      child.cet = node.cet;
    }
    if (node.adr) child.adr = node.adr;
    if (node.cites) child.cites = node.cites;
    if (node.dug) child.dug = node.dug;
    if (node.eutr) child.eutr = node.eutr;
    if (node.reach) child.reach = node.reach;
    if (node.cbam) child.cbam = node.cbam;
    if (node.cont) child.cont = node.cont;
    if (node.meur) child.meur = node.meur;
    if (node.fgas) child.fgas = node.fgas;
    if (child.children) {
      applyInfoToChildren(child);
    }
  });
}

export const useNomenclatureStore = defineStore('nomenclature', () => {
  const tree = ref<Nomenclature | undefined>(undefined);

  const nomenclature = computed(() => {
    let nomenc: Nomenclature | undefined = tree.value;
    if (nomenc && !usePreferencesStore().nomenclature.displaySections) {
      let nomencWithoutSection: NomenclatureNode[] = [];
      tree.value?.forEach((element: NomenclatureNode) => {
        nomencWithoutSection = nomencWithoutSection.concat(element.children ?? []);
      });
      nomenc = nomencWithoutSection;
    }
    return nomenc;
  });

  function clearNomenclature() {
    tree.value = undefined;
  }

  async function requestNomenc() {
    const response = await reqNomencList('eu', usePreferencesStore().interface.lang);
    const sortedNomencsList = response.sort(
      (a: { start_date: string }, b: { start_date: string }) =>
        new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
    );
    try {
      const nomenclatureTree = (await reqNomenc('/data/' + sortedNomencsList[0].swift_url)) as Nomenclature;
      if (!nomenclatureTree) return;
      nomenclatureTree.forEach((element) => {
        element.isSection = true;
      });
      nomenclatureTree.forEach((node) => {
        applyInfoToChildren(node);
      });
      tree.value = nomenclatureTree;
    } catch (error) {
      console.error('Error while fetching nomenclature', error);
    }
  }

  return {
    nomenclature,
    clearNomenclature,
    requestNomenc
  };
});
